import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { useToast } from "@dash/toast";
import { nanoid } from "nanoid";
import { useState } from "react";

import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";

type Props = {
  onClose: () => void;
  onCreated: () => void;
};

export const NewCodeGenerationOrderDialog = ({ onClose, onCreated }: Props) => {
  const [orderId] = useState(() => nanoid());
  const [groupName, setGroupName] = useState("");
  const [email, setEmail] = useState("");
  const [quantity, setQuantity] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [validationError, setValidationError] = useState<string>();
  const toast = useToast();
  const mutation = useClientManager("loyaltyPoints")
    .issueCodeGenerationOrder()
    .useMutation({
      onSuccess: async (_data, _variables, context) => {
        await context.queryClient.invalidateQueries(["loyaltyPoints", "codeGenerationOrdersQuery"]);
        toast.info("Utworzono zlecenie generowania kodów.");
        onCreated();
      },
      onError: (error) => toast.error("Nie udało się utworzyć zlecenia generowania kodów", String(error)),
    });

  const create = () => {
    if (mutation.isLoading) return;
    const count = Number(quantity);
    const expiry = new Date(validUntil);
    if (!groupName.trim()) {
      setValidationError("Podaj nazwę grupy.");
      return;
    }
    if (!/^\d+$/.test(quantity) || !Number.isSafeInteger(count) || count <= 0) {
      setValidationError("Liczba kodów musi być dodatnią liczbą całkowitą.");
      return;
    }
    if (!Number.isFinite(expiry.getTime()) || expiry.getTime() <= Date.now()) {
      setValidationError("Wybierz przyszłą datę i godzinę ważności zlecenia generowania kodów.");
      return;
    }
    const recipientEmail = email.trim() ? Serializers.email.deserialize(email.trim()) : undefined;
    if (recipientEmail?.isFailure) {
      setValidationError("Podaj poprawny adres e-mail.");
      return;
    }
    setValidationError(undefined);
    mutation.mutate({
      orderId,
      groupName: groupName.trim(),
      quantity: count,
      validUntil: expiry,
      email: recipientEmail?.value,
    });
  };

  return (
    <Dialog header="Nowe zlecenie generowania kodów" onClose={() => !mutation.isLoading && onClose()}>
      <div css={{ display: "flex", flexDirection: "column", gap: "1rem", minWidth: "280px" }}>
        <TextField
          autoFocus
          label="Nazwa grupy"
          value={groupName}
          onChange={setGroupName}
          isDisabled={mutation.isLoading}
        />
        <TextField
          label="Liczba kodów"
          value={quantity}
          onChange={setQuantity}
          inputElementProps={{ inputMode: "numeric" }}
          isDisabled={mutation.isLoading}
        />
        <label css={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          Ważność zlecenia generowania kodów
          <input
            type="datetime-local"
            value={validUntil}
            onChange={(event) => setValidUntil(event.target.value)}
            disabled={mutation.isLoading}
          />
        </label>
        <p>Termin, do którego zlecenie generowania kodów może zostać zrealizowane.</p>
        <TextField
          label="Adres e-mail (opcjonalnie)"
          value={email}
          onChange={setEmail}
          inputElementProps={{ inputMode: "email", autoComplete: "email" }}
          isDisabled={mutation.isLoading}
        />
        <p>Na podany adres wyślemy powiadomienie z linkiem do pobrania kodów.</p>
        {validationError && <p role="alert">{validationError}</p>}
        <Button onClick={create} isLoading={mutation.isLoading} isDisabled={mutation.isLoading}>
          Utwórz zlecenie generowania kodów
        </Button>
      </div>
    </Dialog>
  );
};
